import { mapValues, sum, uniq, uniqWith } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { distanceEquals } from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

import { AggregatedTrussStatistics } from '../components/pages/gridTypes';
import { RootState } from '../store';
import { getAggregatedTrussStatisticsByBundleGuid, getStatusesByBundleGuid } from './bundles';
import { getBundlesByScheduleGroupGuid } from './getBundlesByScheduleGroupGuid';

export const getAggregatedTrussStatisticsByScheduleGroupGuid = createSelector(
  (state: RootState) => state.scheduleGroups,
  getBundlesByScheduleGroupGuid,
  getAggregatedTrussStatisticsByBundleGuid,

  (
    scheduleGroups,
    bundlesByScheduleGroupGuid,
    aggregatedTrussStatisticsByBundleGuid,
  ): { [scheduleGroupGuid: string]: AggregatedTrussStatistics } =>
    mapValues(scheduleGroups, (scheduleGroup) => {
      // Do not show values unless all are loaded - we do not want to provide misleading information
      const allTrussesForScheduleGroupAreLoaded = bundlesByScheduleGroupGuid[scheduleGroup.guid].every(
        (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].thicknesses.length > 0,
      );
      if (!allTrussesForScheduleGroupAreLoaded)
        return {
          boardFeet: null,
          linearFeet: null,
          trussCount: sum(
            bundlesByScheduleGroupGuid[scheduleGroup.guid]?.flatMap(
              (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].trussCount,
            ),
          ),
          thicknesses: [],
          widths: [],
          heights: [],
        };

      return {
        boardFeet: sum(
          bundlesByScheduleGroupGuid[scheduleGroup.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].boardFeet ?? 0,
          ),
        ),
        linearFeet: sum(
          bundlesByScheduleGroupGuid[scheduleGroup.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].linearFeet ?? 0,
          ),
        ),
        trussCount: sum(
          bundlesByScheduleGroupGuid[scheduleGroup.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].trussCount,
          ),
        ),
        heights: uniqWith(
          bundlesByScheduleGroupGuid[scheduleGroup.guid]
            ?.flatMap((bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].heights)
            .filter(notNullish),
          distanceEquals,
        ),
        widths: uniqWith(
          bundlesByScheduleGroupGuid[scheduleGroup.guid]
            ?.flatMap((bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].widths)
            .filter(notNullish),
          distanceEquals,
        ),
        thicknesses: uniqWith(
          bundlesByScheduleGroupGuid[scheduleGroup.guid]
            ?.flatMap((bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].thicknesses)
            .filter(notNullish),
          distanceEquals,
        ),
      };
    }),
);

export const getStatusesByScheduleGroupGuid = createSelector(
  (state: RootState) => state.scheduleGroups,
  getBundlesByScheduleGroupGuid,
  getStatusesByBundleGuid,

  (scheduleGroups, bundlesByScheduleGroupGuid, statusesByBundleGuid) =>
    mapValues(scheduleGroups, (scheduleGroup) =>
      uniq(bundlesByScheduleGroupGuid[scheduleGroup.guid]?.flatMap((bundle) => statusesByBundleGuid[bundle.guid])),
    ),
);

export const getTruckloadsByScheduleGroupGuid = createSelector(
  (state: RootState) => state.scheduleGroups,
  getBundlesByScheduleGroupGuid,
  (state: RootState) => state.truckloads,

  (scheduleGroups, bundlesByScheduleGroupGuid, truckloads) =>
    mapValues(scheduleGroups, (scheduleGroup) =>
      uniq(bundlesByScheduleGroupGuid[scheduleGroup.guid].map((bundle) => bundle.truckloadGuid))
        .filter(notNullish)
        .map((truckloadGuid) => truckloads[truckloadGuid])
        .filter(notNullish),
    ),
);
